import { useState } from 'react';
import { useDebounced } from '@client/new/hooks/useDebounced';
import {
  SwapCatalogExercisesQuery,
  useSwapCatalogExercisesQuery,
} from 'generated/graphql';
import { SwapProposal, exerciseNameKey } from './swaps.domain';

const MIN_SWAP_SEARCH_CHARS = 3;

export type SwapCatalogHit =
  SwapCatalogExercisesQuery['swapCatalogExercises'][number];

export type SwapCatalogSearchState =
  | { type: 'idle' }
  | { type: 'results'; query: string; items: SwapCatalogHit[] }
  | { type: 'error' };

export type SwapCatalogSearch = {
  query: string;
  state: SwapCatalogSearchState;
  searching: boolean;
  onQueryChange: (value: string) => void;
  retry: () => void;
};

export const useSwapCatalogSearch = ({
  influencerId,
  exerciseName,
  proposals,
}: {
  influencerId: number;
  exerciseName: string;
  proposals: SwapProposal[];
}): SwapCatalogSearch => {
  const [query, setQuery] = useState('');
  const trimmed = query.trim();
  const debouncedQuery = useDebounced(trimmed, 250);

  const skip =
    trimmed !== debouncedQuery || trimmed.length < MIN_SWAP_SEARCH_CHARS;

  const result = useSwapCatalogExercisesQuery({
    variables: { q: debouncedQuery, influencerId },
    skip,
    fetchPolicy: 'network-only',
  });

  const items = filterOutSelectedSwapsAndOgExercise(
    result.data?.swapCatalogExercises ?? [],
    exerciseName,
    proposals,
  );

  const state: SwapCatalogSearchState = skip
    ? { type: 'idle' }
    : result.error
      ? { type: 'error' }
      : result.loading
        ? { type: 'idle' }
        : { type: 'results', query: debouncedQuery, items };

  return {
    query,
    state,
    searching: result.loading,
    onQueryChange: setQuery,
    retry: () => void result.refetch().catch(() => undefined),
  };
};

// Matches on `hit.exercise.name` because that is the name the proposal is
// built from - keying the exclusion off the catalog row's `name` would let a
// hit stay addable if the two ever drifted apart.
const filterOutSelectedSwapsAndOgExercise = (
  hits: SwapCatalogHit[],
  exerciseName: string,
  proposals: SwapProposal[],
): SwapCatalogHit[] => {
  const excludedKeys = new Set(
    [exerciseName, ...proposals.map((proposal) => proposal.name)].map(
      exerciseNameKey,
    ),
  );
  return hits.filter(
    (hit) => !excludedKeys.has(exerciseNameKey(hit.exercise.name)),
  );
};
